import React from 'react'
import moment from 'moment'

import { ActiveTab } from '@/app/constants/tab'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { ActivityModel } from '@/modules/drivers/types/activities'
import {
  hasVehicle,
  withVehicleFromActivities,
} from '@/modules/drivers/utils/eventVehicle'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import {
  applyEventTableUpdate,
  relinkTmsDocuments,
} from '@/modules/drivers/utils/eventTmsPair'
import { removeTripEventsAt } from '@/modules/drivers/utils/eventListEdits'
import { toast } from '@/lib/toasts/api'
import BusinessTripEditAllowances from '../components/edit/BusinessTripEditAllowances'
import BusinessTripEditInvoices from '../components/edit/BusinessTripEditInvoices'
import BusinessTripEditAdvancePayments from '../components/edit/BusinessTripEditAdvancePayments'
import BusinessTripEditAlimentation from '../components/edit/BusinessTripEditAlimentation'
import BusinessTripEditSummary from '../components/edit/BusinessTripEditSummary'
import { EditBusinessTripTabs } from './tabs/EditBusinessTripTabs'
import BusinessTripEditActivityChart from './tabs/chart/BusinessTripEditActivityChart'
import SummaryModal from './tabs/summary/SummaryModal'
import { useEditBusinessTrip } from '../context/EditBusinessTripContext'
import SettingsModal from './tabs/settings/SettingsModal'
import { EditBusinessTripFormProvider } from '../context/EditBusinessTripFormContext'
import BusinessTripEditAccommodations from './tabs/accomodations/BusinessTripEditAccommodations'
import BusinessTripEditEvents from './tabs/events/BusinessTripEditEvents'
import BusinessTripEditGpsView from './tabs/data/BusinessTripEditGpsView'
import BusinessTripEditTmsView from './tabs/data/BusinessTripEditTmsView'
import BusinessTripEditTachographView from './tabs/data/BusinessTripEditTachographView'
import { BordersView } from '@/modules/drivers/pages/components/businessTrip/BordersView'
import { CardLoadsView } from '@/modules/drivers/pages/components/businessTrip/CardLoadsView'
import { useGlobalDateRange } from './tabs/data/useGlobalDateRange'
import {
  affectsAllowances,
  eventsAffectAllowances,
  toExchangeRateDate,
  type GenerateAllowancesOptions,
} from '../hooks/useBusinessTripAllowancesSync'

const EditBusinessTripContent = ({
  formData,
  setFormData,
  validationErrors,
  companyId,
  driverId,
  setValidationErrors,
  tripId,
  driverName,
  onEventsChanged,
  onRegenerateAllowances,
}: {
  formData: any
  setFormData: (updater: any) => void
  validationErrors: Record<string, any>
  companyId: string
  driverId: string
  setValidationErrors: (updater: any) => void
  tripId: string
  driverName?: string | null
  onEventsChanged: () => void
  onRegenerateAllowances: (options?: GenerateAllowancesOptions) => Promise<any>
}) => {
  const { t } = useLocalize()
  const mergeAndSortEvents = (existing: any[], newEvents: any[]) =>
    [...existing, ...newEvents].sort((a: any, b: any) => {
      if (a.date < b.date) return -1
      if (a.date > b.date) return 1
      return 0
    })

  const { generateAccommodations } = useBusinessTripService()
  const { getDriverActivity } = useDriverActivityService()
  const dayActivities = React.useRef(
    new Map<string, Promise<ReadonlyArray<ActivityModel>>>(),
  )

  // The events tab has no activities loaded, so a moved event without a vehicle
  // fetches just its own day - the same day WPF picks the vehicle from.
  const resolveEventVehicle = (movedTo: any) => {
    const date = moment(movedTo)
    if (!date.isValid()) return

    const day = date.format('YYYY-MM-DD')
    const key = `${driverId}|${day}`
    let pending = dayActivities.current.get(key)
    if (!pending) {
      pending = getDriverActivity({
        companyId,
        driverId: Number(driverId),
        startTime: day,
        endTime: date.clone().add(1, 'day').format('YYYY-MM-DD'),
        includeTrips: false,
      })
        .then((data) => data?.activities || [])
        .catch(() => {
          dayActivities.current.delete(key)
          return []
        })
      dayActivities.current.set(key, pending)
    }

    pending.then((activities) => {
      setFormData((prev: any) => {
        const current: any[] = prev.events || []
        const next = current.map((e: any) =>
          moment(e.date).isSame(date, 'minute')
            ? withVehicleFromActivities(e, activities)
            : e,
        )
        return next.some((e, i) => e !== current[i])
          ? { ...prev, events: next }
          : prev
      })
    })
  }
  const {
    setIsOpenSummary,
    isOpenSummary,
    isOpenSettings,
    setIsOpenSettings,
    refreshData,
    showTachographView,
    setShowTachographView,
    showTmsView,
    setShowTmsView,
    showGpsView,
    setShowGpsView,
    showBordersView,
    setShowBordersView,
    showCardLoadsView,
    setShowCardLoadsView,
  } = useEditBusinessTrip()
  // WPF bierze okres z globalnego filtra dat (IContextService.DateFrom/DateTo).
  const globalDateRange = useGlobalDateRange()

  const [selectedTab, setSelectedTab] = React.useState<ActiveTab>(
    ActiveTab.NEW_DELEGATION_ACTIVITY_CHART,
  )
  const [modifiedSections, setModifiedSections] = React.useState<Set<string>>(
    new Set(),
  )
  const updateArrayItem = (
    arrayName: string,
    index: number,
    updates: any,
    baseline?: any,
  ) => {
    let updatedArray: any[] = []
    setFormData((prev: any) => {
      const array = prev[arrayName] || []
      if (arrayName === 'events') {
        updatedArray = applyEventTableUpdate(array, index, updates, baseline)
      } else {
        updatedArray = [...array]
        updatedArray[index] = { ...updatedArray[index], ...updates }
      }
      return {
        ...prev,
        [arrayName]: updatedArray,
      }
    })
    setModifiedSections((prev) => new Set(prev).add(arrayName))

    if (
      arrayName === 'events' &&
      updates?.date &&
      !hasVehicle({ ...(formData?.events || [])[index], ...updates })
    ) {
      resolveEventVehicle(updates.date)
    }

    if (
      arrayName === 'events' &&
      affectsAllowances((formData?.[arrayName] || [])[index], updates)
    ) {
      onEventsChanged()
    }

    if (arrayName === 'events' && validationErrors.events) {
      const hasEvents = updatedArray.length > 0
      const hasBegin = updatedArray.some(
        (e: any) =>
          e.tripEvent === TripEventType.Begin ||
          e.tripEvent === 'Begin' ||
          e.tripEvent === 1,
      )
      const hasFinish = updatedArray.some(
        (e: any) =>
          e.tripEvent === TripEventType.Finish ||
          e.tripEvent === 'Finish' ||
          e.tripEvent === 2,
      )

      if (hasEvents && hasBegin && hasFinish) {
        setValidationErrors((prev) => {
          const updatedErrors = { ...prev }
          delete updatedErrors.events
          return updatedErrors
        })
      }
    }
  }

  // Add new item to array
  const addArrayItem = (arrayName: string, newItem: any) => {
    let updatedArray: any[] = []
    setFormData((prev: any) => {
      const array = prev[arrayName] || []
      updatedArray = [...array, newItem]
      return {
        ...prev,
        [arrayName]: updatedArray,
      }
    })
    setModifiedSections((prev) => new Set(prev).add(arrayName))

    if (arrayName === 'events') {
      onEventsChanged()
    }

    // Clear validation error for events when user adds events
    if (arrayName === 'events' && validationErrors.events) {
      const hasEvents = updatedArray.length > 0
      const hasBegin = updatedArray.some(
        (e: any) =>
          e.tripEvent === TripEventType.Begin ||
          e.tripEvent === 'Begin' ||
          e.tripEvent === 1,
      )
      const hasFinish = updatedArray.some(
        (e: any) =>
          e.tripEvent === TripEventType.Finish ||
          e.tripEvent === 'Finish' ||
          e.tripEvent === 2,
      )

      if (hasEvents && hasBegin && hasFinish) {
        setValidationErrors((prev) => {
          const updatedErrors = { ...prev }
          delete updatedErrors.events
          return updatedErrors
        })
      }
    }
  }

  // Remove items from array
  const removeArrayItems = (arrayName: string, indices: number[]) => {
    setFormData((prev: any) => {
      const array = prev[arrayName] || []
      const updated =
        arrayName === 'events'
          ? removeTripEventsAt(array, indices)
          : array.filter((_: any, index: number) => !indices.includes(index))
      return {
        ...prev,
        [arrayName]: updated,
      }
    })
    setModifiedSections((prev) => new Set(prev).add(arrayName))

    if (arrayName === 'events') {
      onEventsChanged()
    }
  }

  const renderTabContent = () => {
    const commonProps = {
      tripData: formData,
      onUpdate: updateFormData,
      onUpdateArrayItem: updateArrayItem,
      onAddArrayItem: addArrayItem,
      onRemoveArrayItems: removeArrayItems,
      companyId,
      validationErrors,
    }
    const handleGenerateAccommodations = async () => {
      try {
        const result: any = await generateAccommodations({
          companyId,
          driverId,
          exchangeRateDate: toExchangeRateDate(formData.exchangeRateDate),
          country: formData.destinationCountry || null,
          events: formData.events || [],
          invoices: formData.invoices || [],
          stopDownloadingRates: formData.stopDownloadingRates || false,
        })

        if (result) {
          const accommodations = Array.isArray(result)
            ? result
            : result.accomodations || result.accommodations || []
          // The endpoint also rebuilds the "Zwrot kosztów noclegu" invoice from
          // the fresh accommodations — keep it instead of dropping it.
          const invoices = Array.isArray(result)
            ? null
            : result.Invoices || result.invoices || null
          setFormData((prev: any) => ({
            ...prev,
            accommodations,
            ...(invoices ? { invoices } : {}),
          }))
          setModifiedSections((prev) => {
            const next = new Set(prev).add('accommodations')
            if (invoices) next.add('invoices')
            return next
          })
          toast.success({
            text: t('businessTrip.accommodations-generated'),
          })
        }
      } catch (err: any) {
        console.error('Error generating accommodations:', err)
        toast.error({
          text:
            err?.response?.data?.message ||
            t('newDelegation.delegation-edit-failed-details'),
        })
      }
    }

    switch (selectedTab) {
      case ActiveTab.NEW_DELEGATION_EVENT:
        return (
          <BusinessTripEditEvents
            {...commonProps}
            driverId={driverId}
            tripId={tripId}
          />
        )
      case ActiveTab.NEW_DELEGATION_ACTIVITY_CHART:
        return (
          <BusinessTripEditActivityChart
            onShowGpsView={() => {
              setShowTmsView(false)
              setShowTachographView(false)
              setShowGpsView(true)
            }}
            onShowTmsView={() => {
              setShowGpsView(false)
              setShowTachographView(false)
              setShowTmsView(true)
            }}
            onShowTachographView={() => {
              setShowGpsView(false)
              setShowTmsView(false)
              setShowTachographView(true)
            }}
            onShowCardLoads={() => setShowCardLoadsView(true)}
            tripData={formData}
            onUpdate={updateFormData}
            onUpdateArrayItem={updateArrayItem}
            onAddArrayItem={addArrayItem}
            onRemoveArrayItems={removeArrayItems}
            companyId={companyId}
            driverId={driverId}
            tripId={tripId}
          />
        )
      case ActiveTab.NEW_DELEGATION_ALLOWANCE:
        return (
          <BusinessTripEditAllowances
            {...commonProps}
            onGenerateAllowances={() =>
              onRegenerateAllowances({ preserveAccommodationInvoices: true })
            }
          />
        )
      case ActiveTab.NEW_DELEGATION_ACCOMODATION:
        return (
          <BusinessTripEditAccommodations
            {...commonProps}
            onGenerateAccommodations={handleGenerateAccommodations}
          />
        )
      case ActiveTab.NEW_DELEGATION_INVOICE:
        return (
          <BusinessTripEditInvoices
            {...commonProps}
            onGenerateInvoices={() => onRegenerateAllowances()}
          />
        )
      case ActiveTab.NEW_DELEGATION_ADVANCE_PAYMENT:
        return <BusinessTripEditAdvancePayments {...commonProps} />
      case ActiveTab.NEW_DELEGATION_ALLIMENTATION:
        return <BusinessTripEditAlimentation {...commonProps} />
      case ActiveTab.NEW_DELEGATION_SUMMARY:
        return (
          <BusinessTripEditSummary tripData={formData} companyId={companyId} />
        )
      default:
        return null
    }
  }
  // Update form data and track modifications
  const updateFormData = (field: string, rawValue: any) => {
    // Imports and generators hand over whole lists; pairs are mirrored here once for all of them.
    const value =
      field === 'events' && Array.isArray(rawValue)
        ? relinkTmsDocuments(rawValue)
        : rawValue
    setFormData((prev: any) => {
      const updated = {
        ...prev,
        [field]: value,
      }
      return updated
    })
    setModifiedSections((prev) => new Set(prev).add(field))

    if (field === 'events' && eventsAffectAllowances(formData?.events, value)) {
      onEventsChanged()
    }

    // Clear validation error for this field when user fixes it
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev }
        delete updated[field]
        return updated
      })
    }
  }

  return (
    <>
      <div className="tw-flex-1 tw-overflow-hidden">
        {showGpsView ? (
          <BusinessTripEditGpsView
            tripData={formData}
            companyId={companyId}
            driverId={driverId}
            onCancel={() => setShowGpsView(false)}
            onSave={(events) => {
              updateFormData('events', mergeAndSortEvents(formData.events || [], events))
              setShowGpsView(false)
            }}
          />
        ) : showTmsView ? (
          <BusinessTripEditTmsView
            tripData={formData}
            companyId={companyId}
            driverId={driverId}
            onCancel={() => setShowTmsView(false)}
            onSave={(events) => {
              updateFormData('events', mergeAndSortEvents(formData.events || [], events))
              setShowTmsView(false)
            }}
          />
        ) : showTachographView ? (
          <BusinessTripEditTachographView
            tripData={formData}
            companyId={companyId}
            driverId={driverId}
            onCancel={() => setShowTachographView(false)}
            onSave={(events) => {
              updateFormData('events', mergeAndSortEvents(formData.events || [], events))
              setShowTachographView(false)
            }}
          />
        ) : showCardLoadsView ? (
          <CardLoadsView
            companyId={companyId}
            driverId={Number(driverId)}
            driverName={driverName ?? undefined}
            dateFrom={moment(globalDateRange.from)}
            dateTo={moment(globalDateRange.to)}
            existingEvents={formData.events || []}
            onClose={() => setShowCardLoadsView(false)}
            onSave={(events) => {
              updateFormData('events', mergeAndSortEvents(formData.events || [], events))
              setShowCardLoadsView(false)
            }}
          />
        ) : showBordersView ? (
          <BordersView
            companyId={companyId}
            driverId={Number(driverId)}
            dateFrom={formData.startDate ? moment(formData.startDate) : moment().subtract(1, 'month')}
            dateTo={formData.endDate ? moment(formData.endDate) : moment()}
            onClose={() => setShowBordersView(false)}
            onSave={(events) => {
              updateFormData('events', mergeAndSortEvents(formData.events || [], events))
              setShowBordersView(false)
            }}
            translate={(key) => t(key) || key}
          />
        ) : (
          <EditBusinessTripTabs
            selectedTab={selectedTab}
            accommodations={formData?.accommodations}
            onSelected={(tab) => setSelectedTab(tab.type)}
           
          >
            <div className="tabs-content--wrapper">{renderTabContent()}</div>
          </EditBusinessTripTabs>
        )}
      </div>
      {isOpenSummary && (
        <SummaryModal
          tripData={formData}
          onClose={() => setIsOpenSummary(false)}
        />
      )}
      {isOpenSettings && (
        <EditBusinessTripFormProvider
          companyId={companyId}
          formData={formData}
          tripId={tripId}
          driverId={driverId}
          refreshData={refreshData}
        >
          <SettingsModal onClose={() => setIsOpenSettings(false)} />
        </EditBusinessTripFormProvider>
      )}
    </>
  )
}

export default EditBusinessTripContent
